import React, { useState } from 'react'
import { TimelineSpan } from '../domain/TimelineSpan'
import Milliseconds from '../helper/milliseconds'
import { Box, BoxBody } from '../ui/box'
import { Col, Row } from '../ui/grid'
import { TraceDetailsStacktrace } from './tracedetails/TraceDetailsStacktrace'

interface Props {
    logs: TimelineSpan[]
}

export type LogLevel = 'error' | 'warning' | 'notice' | 'deprecated'

const E_WARNING = 2
const E_NOTICE = 8
const E_USER_WARNING = 512
const E_USER_NOTICE = 1024
const E_DEPRECATED = 8192
const E_USER_DEPRECATED = 16384

export const logLevel = (level: string | undefined): LogLevel => {
    switch (Number(level)) {
        case E_WARNING:
        case E_USER_WARNING:
            return 'warning'
        case E_NOTICE:
        case E_USER_NOTICE:
            return 'notice'
        case E_DEPRECATED:
        case E_USER_DEPRECATED:
            return 'deprecated'
        default:
            return 'error'
    }
}

export const logMessage = (level: LogLevel, type: string | undefined, message: string): { type: string | null; message: string } => {
    if (level !== 'error') {
        return { type: null, message }
    }

    if (type === 'Error' && message.startsWith('Uncaught ')) {
        const index = message.indexOf(':')
        if (index >= 10) {
            return { type: message.substring(9, index), message: message.substring(index + 1).trimStart() }
        }
    }

    return { type: type || null, message }
}

export interface LogEntry {
    level: string | null
    type: string | null
    message: string
    file?: string
    line?: string
    code?: string
}

export const logEntry = (span: TimelineSpan): LogEntry => {
    const annotations = span.annotations || {}

    if (span.cat === 'log') {
        return {
            level: annotations['log.level'] || null,
            type: null,
            message: annotations['log.msg'] || span.name,
            file: annotations['log.file'],
            line: annotations['log.line'],
        }
    }

    const level = logLevel(annotations['error.level'])

    return {
        level,
        ...logMessage(level, annotations['error.type'], annotations['error.msg'] || span.name),
        file: annotations['error.file'],
        line: annotations['error.line'],
        code: annotations['error.code'],
    }
}

const levelLabelClass = (level: string): string => {
    switch (level) {
        case 'emergency':
        case 'alert':
        case 'critical':
        case 'error':
            return 'label-danger'
        case 'warning':
            return 'label-warning'
        case 'notice':
        case 'info':
        case 'deprecated':
            return 'label-info'
        default:
            return 'label-default'
    }
}

const LogRow: React.FC<{ span: TimelineSpan }> = ({ span }) => {
    const [expanded, setExpanded] = useState(false)
    const annotations = span.annotations || {}
    const { level, type, message, file, line, code } = logEntry(span)
    const hasStack = (span.stack?.length || 0) > 0 || typeof annotations['error.trace'] === 'string'

    return (
        <>
            <tr className="trace-log-row" onClick={hasStack ? () => setExpanded(!expanded) : undefined}>
                <td className="number short">
                    <Milliseconds value={+span.cs} showNoValueAsHyphen={false} />
                </td>
                <td>{level ? <span className={'label ' + levelLabelClass(level)}>{level}</span> : null}</td>
                <td>
                    {type ? <strong>{type}</strong> : null}
                    {code && code !== '0' ? <small className="text-muted tw-ml-4">Code {code}</small> : null}
                    {(span.ec || 0) > 1 ? <span className="label label-outline-default tw-ml-4">{span.ec}x</span> : null}
                    <div>{message}</div>
                    {file ? (
                        <small className="text-muted">
                            {file}
                            {line ? ':' + line : ''}
                        </small>
                    ) : null}
                </td>
                <td className="number short">{hasStack ? <i className={'fa fa-chevron-' + (expanded ? 'up' : 'down')} /> : null}</td>
            </tr>
            {expanded ? (
                <tr className="trace-log-details">
                    <td colSpan={4}>
                        {(span.stack?.length || 0) > 0 ? (
                            <TraceDetailsStacktrace stack={span.stack || []} />
                        ) : (
                            <pre>{annotations['error.trace']}</pre>
                        )}
                    </td>
                </tr>
            ) : null}
        </>
    )
}

const Logs: React.FC<Props> = ({ logs }) => (
    <Row>
        <Col>
            <Box>
                <BoxBody>
                    {logs.length === 0 ? (
                        <p className="text-muted">This trace contains no logs.</p>
                    ) : (
                        <table className="table table-condensed">
                            <thead>
                                <tr>
                                    <th className="number short">Time</th>
                                    <th>Level</th>
                                    <th>Message</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(span => (
                                    <LogRow key={span.id} span={span} />
                                ))}
                            </tbody>
                        </table>
                    )}
                </BoxBody>
            </Box>
        </Col>
    </Row>
)

export default Logs
